import React from 'react';
import { compose } from 'redux';

import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { PrimaryButton, NamedLink, Form, FieldMessageComposer } from '../../components';
import { hasBlockingAttachments } from '../../ducks/messageAttachments.duck';
import { Form as FinalForm } from 'react-final-form';
import setFieldData from 'final-form-set-field-data';
import { IoMdChatbubbles } from 'react-icons/io';
import { BsArchive } from 'react-icons/bs';

import css from './ApplicationPanel.css';

const ActionsComponent = props => {
  // Which "send and continue" button submitted. A ref, not state: the button
  // sets it and submits in the same click handler, and state set there would
  // still be stale when the submit logic reads it.
  const afterSkipRef = React.useRef(false);
  const [showMessageField, setShowMessageField] = React.useState(false);
  const [showSkipConfirmation, setShowSkipConfirmation] = React.useState(false);
  const messageFieldRef = React.useRef(null);

  React.useEffect(() => {
    if (showMessageField && messageFieldRef.current) {
      // Focus the textarea after a short delay to ensure it's rendered
      setTimeout(() => {
        const textarea = messageFieldRef.current.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 100);
    }
  }, [showMessageField]);

  const handleSubmitLogic = values => {
    const enhancedValues = {
      ...values,
      afterSkip: afterSkipRef.current,
    };
    props.onSubmit(enhancedValues);
    setShowMessageField(false);
  };

  return (
    <FinalForm
      {...props}
      mutators={{ setFieldData }}
      onSubmit={handleSubmitLogic}
      render={fieldRenderProps => {
        const {
          formId,
          handleSubmit,
          intl,
          authorDisplayName,
          values,
          transaction,
          transactionId,
          showActionButtons,
          showAcceptAfterSkipButton,
          sendAcceptApplicationInProgress,
          showProfileListingInProgress,
          sendCreateReverseTransactionInProgress,
          sendSkipApplicationInProgress,
          sendAcceptAfterSkipInProgress,
          onSkipApplication,
          showToNewTransactionButton,
          isCustomer,
          stagedAttachments,
          onUploadAttachment,
          onRemoveAttachment,
        } = fieldRenderProps;

        // Attachments are opt-in: the field shows the attach control only when
        // the page wires the handlers.
        const showAttachments = !!onUploadAttachment && !!onRemoveAttachment;
        const attachments = stagedAttachments || [];

        const buttonsDisabled =
          sendAcceptApplicationInProgress ||
          showProfileListingInProgress ||
          sendCreateReverseTransactionInProgress ||
          sendSkipApplicationInProgress ||
          sendAcceptAfterSkipInProgress;

        const newTransactionId = transaction.attributes.protectedData.newTransactionId;

        const messagePlaceholder = intl.formatMessage(
          {
            id: 'ApplicationPanel.messagePlaceholder',
          },
          { authorDisplayName }
        );

        const messageField = (
          <FieldMessageComposer
            id={formId ? `${formId}.message` : 'message'}
            name="message"
            label={intl.formatMessage({ id: 'ApplicationPanel.messageLabel' })}
            placeholder={messagePlaceholder}
            message={values.message}
            textareaClassName={css.acceptTextarea}
            stagedAttachments={attachments}
            onUploadAttachment={onUploadAttachment}
            onRemoveAttachment={onRemoveAttachment}
          />
        );
        // A non-empty message is required, enforced by disabling submit rather
        // than a field validator: the field is autofocused when the form opens,
        // and a blur-time "required" error would shift the layout under the
        // attach button, swallowing the first click. In-flight uploads must
        // finish (and failed ones be removed) before the message can go out.
        const hasMessage = !!(values.message || '').trim();
        const submitDisabled =
          !hasMessage || (showAttachments && hasBlockingAttachments(attachments));

        // Deliberately not type="submit": in a sandboxed tab without allow-forms
        // the browser drops a native form submission before any submit event
        // fires, so onSubmit would never run. Submitting from the click keeps
        // accepting independent of that.
        const submitWith = afterSkip => {
          afterSkipRef.current = afterSkip;
          handleSubmit();
        };

        return (
          <Form className={css.form} onSubmit={handleSubmit}>
            {showActionButtons && (
              <div className={css.formWrapper}>
                <div className={css.actionButtonsGrid}>
                  <button
                    type="button"
                    className={`${css.actionButtonNext} ${
                      showSkipConfirmation ? css.actionButtonConfirming : ''
                    }`}
                    onClick={() => {
                      if (showSkipConfirmation) {
                        onSkipApplication(transactionId);
                        setShowSkipConfirmation(false);
                      } else {
                        setShowSkipConfirmation(true);
                        setShowMessageField(false);
                      }
                    }}
                    disabled={buttonsDisabled}
                  >
                    <span className={css.actionIcon}>
                      <BsArchive />
                    </span>
                    <span className={css.actionTitle}>
                      <FormattedMessage
                        id={
                          showSkipConfirmation
                            ? 'ApplicationPanel.confirmNotThisTime'
                            : 'ApplicationPanel.notThisTime'
                        }
                      />
                    </span>
                    <span className={css.actionSubtext}>
                      <FormattedMessage
                        id={
                          showSkipConfirmation
                            ? 'ApplicationPanel.clickAgainToConfirm'
                            : 'ApplicationPanel.notRightFit'
                        }
                      />
                    </span>
                  </button>

                  <button
                    type="button"
                    className={css.actionButtonAccept}
                    onClick={() => {
                      setShowMessageField(true);
                      setShowSkipConfirmation(false);
                    }}
                    disabled={buttonsDisabled}
                  >
                    <span className={css.actionIcon}>
                      <IoMdChatbubbles />
                    </span>
                    <span className={css.actionTitle}>
                      <FormattedMessage id="ApplicationPanel.startConversation" />
                    </span>
                    <span className={css.actionSubtext}>
                      <FormattedMessage id="ApplicationPanel.continueWithApplicant" />
                    </span>
                  </button>
                </div>
                {showMessageField && (
                  <div className={css.messageFieldWrapper} ref={messageFieldRef}>
                    {messageField}
                    <div className={css.messageActions}>
                      <PrimaryButton
                        className={css.submitButton}
                        inProgress={
                          sendAcceptApplicationInProgress ||
                          showProfileListingInProgress ||
                          sendCreateReverseTransactionInProgress
                        }
                        disabled={submitDisabled}
                        type="button"
                        onClick={() => submitWith(false)}
                      >
                        <FormattedMessage id="ApplicationPanel.sendAndContinue" />
                      </PrimaryButton>
                    </div>
                  </div>
                )}
              </div>
            )}
            {showAcceptAfterSkipButton && (
              <div className={css.formWrapper}>
                <div className={css.messageFieldWrapper}>
                  {messageField}
                  <div className={css.messageActions}>
                    <PrimaryButton
                      className={css.submitButton}
                      inProgress={
                        sendAcceptApplicationInProgress ||
                        showProfileListingInProgress ||
                        sendCreateReverseTransactionInProgress
                      }
                      disabled={submitDisabled}
                      type="button"
                      onClick={() => submitWith(true)}
                    >
                      <FormattedMessage id="ApplicationPanel.sendAndContinue" />
                    </PrimaryButton>
                  </div>
                </div>
              </div>
            )}
            {showToNewTransactionButton && (
              <div className={css.toNewProjectWrapper}>
                <NamedLink
                  className={css.toNewTransactionButton}
                  name={isCustomer ? 'SaleDetailsPage' : 'OrderDetailsPage'}
                  params={{ id: newTransactionId }}
                >
                  <FormattedMessage id="ApplicationPanel.toNewTransaction" />
                </NamedLink>
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

const Actions = compose(injectIntl)(ActionsComponent);

export default Actions;
